import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Info,
  FileText,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BackgroundUploader from "@/components/BackgroundUploader";
import DevicePreview from "@/components/DevicePreview";
import ColorOverridePicker from "@/components/ColorOverridePicker";
import FontPicker from "@/components/FontPicker";
import AppearanceCustomizer from "@/components/AppearanceCustomizer";
import StatusPill from "@/components/StatusPill";
import GrandDrawPanel from "@/components/GrandDrawPanel";
import QuizDrawPanel from "@/components/QuizDrawPanel";
import TestLinkCard from "@/components/TestLinkCard";
import SpinWheel from "@/components/SpinWheel";
import SpinWheelPremium from "@/components/SpinWheelPremium";
import api, { formatApiError } from "@/lib/api";

/**
 * MerchantGameEditor — lightweight editor for merchants.
 *
 * Merchants can ONLY edit cosmetic fields (title, description, messages,
 * dates, background image, text colors) and the `active` toggle. Anything
 * else (prize segments, probabilities, stock, CTAs, game type) stays
 * read-only — those require an admin to prevent contest-rule tampering.
 */
export default function MerchantGameEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [game, setGame] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    api
      .get(`/games/${id}`)
      .then((r) => setGame(r.data))
      .catch((e) => setLoadError(formatApiError(e)));
  }, [id]);

  function patch(fields) {
    setGame((g) => ({ ...g, ...fields }));
  }

  function patchFont(element, cfg) {
    setGame((g) => ({ ...g, fonts: { ...(g.fonts || {}), [element]: cfg } }));
  }

  function patchSegmentInstructions(idx, value) {
    setGame((g) => {
      if (!g) return g;
      const segs = (g.segments || []).map((s, i) =>
        i === idx ? { ...s, instructions: value || null } : s,
      );
      return { ...g, segments: segs };
    });
  }

  function patchSegmentPopupInfo(idx, value) {
    setGame((g) => {
      if (!g) return g;
      const segs = (g.segments || []).map((s, i) =>
        i === idx ? { ...s, popup_info: value || null } : s,
      );
      return { ...g, segments: segs };
    });
  }

  function patchSegmentColor(idx, value) {
    setGame((g) => {
      if (!g) return g;
      const segs = (g.segments || []).map((s, i) =>
        i === idx ? { ...s, color: value } : s,
      );
      return { ...g, segments: segs };
    });
  }

  async function downloadTechnicalPdf() {
    try {
      const res = await api.get(`/games/${id}/technical-pdf`, {
        params: { lang: i18n.language?.startsWith("en") ? "en" : "fr" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `QRISE-Fiche-${game?.slug || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function save() {
    if (!game || saving) return;
    setSaving(true);
    // Only send the fields merchants are allowed to update
    const allowed = {
      title: game.title,
      description: game.description,
      win_message: game.win_message,
      lose_message: game.lose_message,
      confirm_title: game.confirm_title || null,
      confirm_body: game.confirm_body || null,
      quiz_intro_text: game.quiz_intro_text || null,
      quiz_winners_label: game.quiz_winners_label || null,
      win_email_message: game.win_email_message,
      rules_content: game.rules_content,
      background_image: game.background_image,
      background_image_tablet: game.background_image_tablet || null,
      background_image_mobile: game.background_image_mobile || null,
      title_color: game.title_color || null,
      description_color: game.description_color || null,
      fonts: game.fonts || null,
      button_bg_color: game.button_bg_color || null,
      button_text_color: game.button_text_color || null,
      button_radius: game.button_radius || null,
      button_continue_label: game.button_continue_label || null,
      button_ready_label: game.button_ready_label || null,
      bg_overlay_opacity: game.bg_overlay_opacity ?? null,
      force_theme: game.force_theme || null,
      confetti_colors: game.confetti_colors?.length ? game.confetti_colors : null,
      wheel_hub_logo: game.wheel_hub_logo ?? null,
      win_image: game.win_image || null,
      og_image: game.og_image || null,
      game_sounds: game.game_sounds ?? null,
      collect_phone: game.collect_phone || null,
      collect_birthdate: game.collect_birthdate ?? null,
      collect_children: game.collect_children || null,
      collect_city: game.collect_city || null,
      button_bg_color_2: game.button_bg_color_2 || null,
      quiz_select_color: game.quiz_select_color || null,
      start_date: game.start_date,
      end_date: game.end_date,
      start_time: game.start_time,
      end_time: game.end_time,
      usable_from_mode: game.usable_from_mode,
      usable_from_date: game.usable_from_date,
      wheel_label_orientation: game.wheel_label_orientation || null,
      // Send the whole segments array unchanged — backend enforces that
      // only the `instructions` field per segment may differ.
      segments: game.segments || [],
      // Pronostic — reward mode + guaranteed prize (merchant-editable)
      ...(game.type === "prediction_match"
        ? {
            prediction_reward_mode: game.prediction_reward_mode || "draw",
            guaranteed_prize_label: game.guaranteed_prize_label || null,
            guaranteed_coupon_code: game.guaranteed_coupon_code || null,
            guaranteed_prize_instructions:
              game.guaranteed_prize_instructions || null,
            guaranteed_claim_days: game.guaranteed_claim_days ?? 30,
            guaranteed_claim_deadline: game.guaranteed_claim_deadline || null,
          }
        : {}),
    };
    try {
      const r = await api.patch(`/games/${id}`, allowed);
      setGame(r.data);
      toast.success(t("common.saved", "Saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-muted/40">
        <Navbar variant="app" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-8">
          <div className="qrise-card p-8 text-center">
            <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto mb-3" />
            <div className="text-base font-semibold mb-2">
              {t("merchant_editor.load_error", "Impossible de charger ce jeu")}
            </div>
            <div className="text-sm text-muted-foreground mb-4">{loadError}</div>
            <Link to="/merchant" className="text-sm underline underline-offset-4">
              {t("merchant_editor.back", "Retour au tableau de bord")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-muted/40">
        <Navbar variant="app" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/40">
      <Navbar variant="app" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/merchant")}
              className="h-9 w-9 rounded-lg border border-border hover:bg-muted/60 flex items-center justify-center"
              data-testid="merchant-editor-back"
              aria-label="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
                {t("merchant_editor.eyebrow", "Éditer mon jeu")}
              </div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {game.title}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <StatusPill status={game.status || (game.active ? "live" : "suspended")} />
            <Button
              variant="outline"
              onClick={downloadTechnicalPdf}
              className="rounded-xl h-10"
              data-testid="merchant-editor-pdf-btn"
              title={t("game_editor.export_pdf_hint", "Télécharge la fiche technique PDF du jeu")}
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {t("game_editor.export_pdf", "Fiche PDF")}
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-xl h-10"
              data-testid="merchant-editor-save-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-1.5" />
              )}
              {t("common.save", "Enregistrer")}
            </Button>
          </div>
        </div>

        {/* Read-only notice */}
        <div
          className="mb-6 rounded-xl border border-border/70 bg-muted/40 px-4 py-3 flex gap-2 text-[13px]"
          data-testid="merchant-editor-notice"
        >
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-muted-foreground" />
          <div className="text-muted-foreground">
            {t(
              "merchant_editor.notice",
              "Vous pouvez modifier le titre, la description, les messages, l'image et les dates. Les lots, probabilités et stocks sont verrouillés : contactez votre administrateur pour les ajuster.",
            )}
          </div>
        </div>

        <div className="mb-6">
          <TestLinkCard gameId={game.id} testIdPrefix="merchant-test-link" />
        </div>

        {/* ---------- Grand tirage de fin de campagne ---------- */}
        {game.grand_draw_enabled && (
          <div className="mb-6">
            <GrandDrawPanel game={game} />
          </div>
        )}

        {/* ---------- Quiz : tirage parmi les sans-faute ---------- */}
        {game.type === "quiz" && (
          <div className="mb-6">
            <QuizDrawPanel game={game} />
          </div>
        )}

        {/* ---------- Texts ---------- */}
        <Section title={t("merchant_editor.texts", "Textes")}>
          <Field label={t("game_editor.field_title", "Titre")}>
            <Input
              value={game.title || ""}
              onChange={(e) => patch({ title: e.target.value })}
              className="rounded-xl"
              data-testid="merchant-editor-title"
            />
          </Field>
          <Field label={t("game_editor.field_description", "Description")}>
            <Textarea
              value={game.description || ""}
              onChange={(e) => patch({ description: e.target.value })}
              rows={3}
              className="rounded-xl"
              data-testid="merchant-editor-description"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field
              label={t("game_editor.field_win_message", "Message gagnant (écran de victoire)")}
            >
              <Textarea
                value={game.win_message || ""}
                onChange={(e) => patch({ win_message: e.target.value })}
                rows={2}
                className="rounded-xl"
                data-testid="merchant-editor-win-msg"
              />
            </Field>
            <Field
              label={t("game_editor.field_lose_message", "Message perdant")}
            >
              <Textarea
                value={game.lose_message || ""}
                onChange={(e) => patch({ lose_message: e.target.value })}
                rows={2}
                className="rounded-xl"
                data-testid="merchant-editor-lose-msg"
              />
            </Field>
          </div>
          {(game.type === "lottery_draw" || game.type === "quiz") && (
            <div className="rounded-xl border border-border/60 p-3 space-y-3" data-testid="merchant-editor-confirm-texts">
              <div className="text-[11px] text-muted-foreground leading-snug">
                {t("game_editor.confirm_hint")}
              </div>
              <Field label={t("game_editor.confirm_title")}>
                <Input
                  value={game.confirm_title || ""}
                  onChange={(e) => patch({ confirm_title: e.target.value || null })}
                  placeholder={t("play.lottery_confirmed_title")}
                  className="rounded-xl"
                  data-testid="merchant-editor-confirm-title"
                />
              </Field>
              <Field label={t("game_editor.confirm_body")}>
                <Textarea
                  value={game.confirm_body || ""}
                  onChange={(e) => patch({ confirm_body: e.target.value || null })}
                  placeholder={t("play.lottery_confirmed_body")}
                  rows={3}
                  className="rounded-xl"
                  data-testid="merchant-editor-confirm-body"
                />
              </Field>
              {game.type === "quiz" && (
                <>
                  <div className="text-[11px] text-muted-foreground leading-snug pt-1 border-t border-border/60">
                    {t("game_editor.quiz_intro_hint")}
                  </div>
                  <Field label={t("game_editor.quiz_intro_text")}>
                    <Textarea
                      value={game.quiz_intro_text || ""}
                      onChange={(e) => patch({ quiz_intro_text: e.target.value || null })}
                      placeholder={t("play.quiz_intro_body")}
                      rows={3}
                      className="rounded-xl"
                      data-testid="merchant-editor-quiz-intro-text"
                    />
                  </Field>
                  <Field label={t("game_editor.quiz_winners_label")}>
                    <Input
                      value={game.quiz_winners_label || ""}
                      onChange={(e) => patch({ quiz_winners_label: e.target.value || null })}
                      placeholder={t("play.quiz_intro_winners", { count: game.winners_count || 1 })}
                      className="rounded-xl"
                      data-testid="merchant-editor-quiz-winners-label"
                    />
                  </Field>
                </>
              )}
            </div>
          )}
          <Field
            label={t(
              "game_editor.field_win_email",
              "Message personnalisé dans l'email du gagnant",
            )}
            hint={t(
              "game_editor.field_win_email_hint",
              "Ex. « Présentez cet email à votre serveur pour récupérer votre lot ».",
            )}
          >
            <Textarea
              value={game.win_email_message || ""}
              onChange={(e) => patch({ win_email_message: e.target.value })}
              rows={3}
              className="rounded-xl"
              data-testid="merchant-editor-win-email-msg"
            />
          </Field>
        </Section>

        {/* ---------- Visuals ---------- */}
        <Section title={t("merchant_editor.visuals", "Visuels")}>
          <div data-testid="merchant-backgrounds-section">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
              {t("bg.responsive_title", "Images de fond par appareil")}
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug mb-3">
              {t(
                "bg.responsive_hint",
                "Le joueur voit automatiquement l'image adaptée à son écran. L'image PC sert de repli si aucune image tablette ou mobile n'est définie.",
              )}
            </p>
            <div className="flex flex-wrap gap-5">
              <BackgroundUploader
                variant="desktop"
                label={t("bg.label_desktop", "Fond PC (par défaut)")}
                value={game.background_image}
                onChange={(v) => patch({ background_image: v })}
              />
              <BackgroundUploader
                variant="tablet"
                label={t("bg.label_tablet", "Fond Tablette")}
                value={game.background_image_tablet}
                onChange={(v) => patch({ background_image_tablet: v })}
                testIdPrefix="merchant-bg-tablet"
              />
              <BackgroundUploader
                variant="mobile"
                label={t("bg.label_mobile", "Fond Mobile")}
                value={game.background_image_mobile}
                onChange={(v) => patch({ background_image_mobile: v })}
                testIdPrefix="merchant-bg-mobile"
              />
            </div>
            <div className="mt-5 pt-4 border-t border-border/60">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
                {t("bg.preview_title", "Aperçu joueur")}
              </div>
              <p className="text-[11px] text-muted-foreground leading-snug mb-3">
                {t(
                  "bg.preview_hint",
                  "Rendu approximatif de la page de jeu publique avec vos fonds, couleurs et polices.",
                )}
              </p>
              <DevicePreview game={game} />
            </div>
          </div>
          <div className="pt-4 border-t border-border/60">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
              {t("game_editor.text_colors", "Couleurs du titre et de la description")}
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug mb-3">
              {t(
                "game_editor.text_colors_hint",
                "Personnalisez pour améliorer la lisibilité sur votre image de fond.",
              )}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <ColorOverridePicker
                label={t("game_editor.title_color", "Titre")}
                value={game.title_color}
                onChange={(v) => patch({ title_color: v })}
                previewText={game.title || "Titre"}
                testIdPrefix="merchant-title-color"
              />
              <ColorOverridePicker
                label={t("game_editor.description_color", "Description")}
                value={game.description_color}
                onChange={(v) => patch({ description_color: v })}
                previewText={
                  game.description
                    ? game.description.slice(0, 28) + "…"
                    : "Description"
                }
                testIdPrefix="merchant-description-color"
              />
            </div>
          </div>
          <div className="pt-4 border-t border-border/60" data-testid="merchant-fonts-section">
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1">
              {t("game_editor.fonts_section", "Typographie")}
            </div>
            <p className="text-[11px] text-muted-foreground leading-snug mb-3">
              {t(
                "game_editor.fonts_hint",
                "Choisissez une police pour chaque élément de la page de jeu publique. Laissez « Par défaut » pour conserver la police du thème.",
              )}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <FontPicker
                label={t("game_editor.font_title", "Titre")}
                value={game.fonts?.title}
                onChange={(v) => patchFont("title", v)}
                previewText={game.title || "Titre"}
                testIdPrefix="merchant-font-title"
              />
              <FontPicker
                label={t("game_editor.font_description", "Description")}
                value={game.fonts?.description}
                onChange={(v) => patchFont("description", v)}
                previewText={
                  game.description
                    ? game.description.slice(0, 28) + "…"
                    : "Description"
                }
                testIdPrefix="merchant-font-description"
              />
              <FontPicker
                label={t("game_editor.font_prizes", "Libellés des lots")}
                value={game.fonts?.prizes}
                onChange={(v) => patchFont("prizes", v)}
                previewText={game.segments?.[0]?.label || t("game_editor.preview_prize")}
                testIdPrefix="merchant-font-prizes"
              />
              <FontPicker
                label={t("game_editor.font_messages", "Messages gagné / perdu")}
                value={game.fonts?.messages}
                onChange={(v) => patchFont("messages", v)}
                previewText={game.win_message || t("game_editor.preview_win")}
                testIdPrefix="merchant-font-messages"
              />
              <FontPicker
                label={t("game_editor.font_buttons", "Boutons")}
                value={game.fonts?.buttons}
                onChange={(v) => patchFont("buttons", v)}
                previewText={t("game_editor.preview_continue")}
                testIdPrefix="merchant-font-buttons"
              />
              <FontPicker
                label={t("game_editor.font_cta", "Actions (CTA)")}
                value={game.fonts?.cta}
                onChange={(v) => patchFont("cta", v)}
                previewText={game.ctas?.[0]?.label || "Suivez-nous"}
                testIdPrefix="merchant-font-cta"
              />
            </div>
          </div>
        </Section>

        {/* ---------- Buttons & effects ---------- */}
        <Section title={t("game_editor.appearance_section", "Boutons & effets")}>
          <AppearanceCustomizer
            game={game}
            patch={patch}
            testIdPrefix="merchant-appearance"
          />
        </Section>

        {/* ---------- Scheduling ---------- */}
        <Section title={t("merchant_editor.scheduling", "Planning")}>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("game_editor.start_date", "Date de début")}>
              <Input
                type="date"
                value={game.start_date || ""}
                onChange={(e) => patch({ start_date: e.target.value || null })}
                className="rounded-xl"
                data-testid="merchant-editor-start-date"
              />
            </Field>
            <Field label={t("game_editor.end_date", "Date de fin")}>
              <Input
                type="date"
                value={game.end_date || ""}
                onChange={(e) => patch({ end_date: e.target.value || null })}
                className="rounded-xl"
                data-testid="merchant-editor-end-date"
              />
            </Field>
            <Field label={t("game_editor.start_time", "Heure d'ouverture")}>
              <Input
                type="time"
                value={game.start_time || ""}
                onChange={(e) => patch({ start_time: e.target.value || null })}
                className="rounded-xl"
                data-testid="merchant-editor-start-time"
              />
            </Field>
            <Field label={t("game_editor.end_time", "Heure de fermeture")}>
              <Input
                type="time"
                value={game.end_time || ""}
                onChange={(e) => patch({ end_time: e.target.value || null })}
                className="rounded-xl"
                data-testid="merchant-editor-end-time"
              />
            </Field>
          </div>
          <p className="text-[11px] text-muted-foreground leading-snug" data-testid="merchant-editor-date-hint">
            {t("game_editor.date_hint")}
          </p>
          <Field
            label={t("game_editor.usable_from_mode", "Lot récupérable")}
            hint={t(
              "game_editor.usable_from_hint",
              "À partir de quand le gagnant peut récupérer son lot.",
            )}
          >
            <Select
              value={game.usable_from_mode || "tomorrow"}
              onValueChange={(v) => patch({ usable_from_mode: v })}
            >
              <SelectTrigger
                className="rounded-xl h-10"
                data-testid="merchant-editor-usable-mode"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="immediately">
                  {t("game_editor.usable_immediately", "Immédiatement")}
                </SelectItem>
                <SelectItem value="tomorrow">
                  {t("game_editor.usable_tomorrow", "Dès le lendemain")}
                </SelectItem>
                <SelectItem value="next_monday">
                  {t("game_editor.usable_next_monday", "Dès le lundi suivant")}
                </SelectItem>
                <SelectItem value="custom">
                  {t("game_editor.usable_custom", "Date personnalisée")}
                </SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {game.usable_from_mode === "custom" && (
            <Field label={t("game_editor.usable_from_date", "Date précise")}>
              <Input
                type="date"
                value={game.usable_from_date || ""}
                onChange={(e) =>
                  patch({ usable_from_date: e.target.value || null })
                }
                className="rounded-xl"
                data-testid="merchant-editor-usable-date"
              />
            </Field>
          )}
        </Section>

        {/* ---------- Spin Wheel — label orientation ---------- */}
        {game.type === "spin_wheel" && (
          <Section
            title={t(
              "game_editor.wheel_label_orientation",
              "Orientation du texte sur la roue",
            )}
          >
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0 space-y-2">
                <Field
                  label={t("game_editor.wheel_label_orientation", "Orientation")}
                  hint={t(
                    "game_editor.wheel_label_orientation_hint",
                    "Passez en vertical si les noms de vos lots sont trop longs pour tenir horizontalement dans une part.",
                  )}
                >
                  <Select
                    value={game.wheel_label_orientation || "horizontal"}
                    onValueChange={(v) =>
                      patch({ wheel_label_orientation: v })
                    }
                  >
                    <SelectTrigger
                      className="rounded-xl h-10"
                      data-testid="merchant-editor-wheel-orientation"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="horizontal">
                        {t("game_editor.wheel_label_horizontal", "Horizontal")}
                      </SelectItem>
                      <SelectItem value="vertical">
                        {t(
                          "game_editor.wheel_label_vertical",
                          "Vertical (radial)",
                        )}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <p className="text-[11px] text-muted-foreground/80 leading-snug">
                  {t(
                    "game_editor.wheel_label_preview_caption",
                    "Aperçu en temps réel avec vos vrais lots →",
                  )}
                </p>
              </div>
              <div
                className="w-[160px] shrink-0"
                data-testid="merchant-wheel-orientation-preview"
              >
                {game.wheel_design === "premium" ? (
                  <SpinWheelPremium
                    segments={game.segments || []}
                    rimColor={game.wheel_rim_color || "#1F4D3A"}
                    curvedText={game.wheel_curved_text}
                    pointerColor={game.button_bg_color || "#D62828"}
                    showPrizeNames={game.wheel_show_prize_names !== false}
                    preview
                  />
                ) : (
                <SpinWheel
                  segments={game.segments || []}
                  showPrizeNames={game.wheel_show_prize_names !== false}
                  labelOrientation={
                    game.wheel_label_orientation || "horizontal"
                  }
                  preview
                />
                )}
              </div>
            </div>
          </Section>
        )}

        {/* ---------- Pronostic — reward mode + guaranteed prize ---------- */}
        {game.type === "prediction_match" && (
          <Section
            title={t("game_editor.pred_reward_mode", "Mode de récompense")}
          >
            <Field
              label={t("game_editor.pred_reward_mode", "Mode de récompense")}
              hint={t(
                "game_editor.pred_reward_mode_hint",
                "Tirage = gagnants tirés au sort parmi les bons pronostics. 100% gagnant = tous les bons pronostiqueurs gagnent le lot garanti.",
              )}
            >
              <Select
                value={game.prediction_reward_mode || "draw"}
                onValueChange={(v) => patch({ prediction_reward_mode: v })}
              >
                <SelectTrigger
                  className="rounded-xl h-10"
                  data-testid="merchant-pred-reward-mode"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draw">
                    {t("game_editor.pred_reward_draw", "Tirage au sort")}
                  </SelectItem>
                  <SelectItem value="all_winners">
                    {t("game_editor.pred_reward_all", "100% gagnant")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {(game.prediction_reward_mode || "draw") === "all_winners" && (
              <div
                className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-4 space-y-4"
                data-testid="merchant-pred-guaranteed-block"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label={t("game_editor.pred_prize_label", "Lot garanti *")}
                  >
                    <Input
                      value={game.guaranteed_prize_label || ""}
                      onChange={(e) =>
                        patch({ guaranteed_prize_label: e.target.value })
                      }
                      placeholder={t(
                        "game_editor.pred_prize_label_ph",
                        "Ex : Un dessert offert",
                      )}
                      className="rounded-xl"
                      data-testid="merchant-pred-prize-label"
                    />
                  </Field>
                  <Field
                    label={t(
                      "game_editor.pred_coupon_code",
                      "Code promo (optionnel)",
                    )}
                    hint={t(
                      "game_editor.pred_coupon_code_hint",
                      "Laissez vide pour générer un code unique par gagnant.",
                    )}
                  >
                    <Input
                      value={game.guaranteed_coupon_code || ""}
                      onChange={(e) =>
                        patch({
                          guaranteed_coupon_code: e.target.value || null,
                        })
                      }
                      className="rounded-xl font-mono"
                      data-testid="merchant-pred-coupon-code"
                    />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field
                    label={t("game_editor.pred_claim_days", "Validité (jours)")}
                    hint={t(
                      "game_editor.pred_claim_days_hint",
                      "Durée pendant laquelle les gagnants peuvent récupérer leur lot après la distribution.",
                    )}
                  >
                    <Input
                      type="number"
                      min={1}
                      value={game.guaranteed_claim_days ?? 30}
                      onChange={(e) =>
                        patch({
                          guaranteed_claim_days: Math.max(
                            1,
                            parseInt(e.target.value) || 30,
                          ),
                        })
                      }
                      className="rounded-xl max-w-[140px]"
                      data-testid="merchant-pred-claim-days"
                    />
                  </Field>
                  <Field
                    label={t(
                      "game_editor.pred_claim_deadline",
                      "Date limite de récupération (optionnel)",
                    )}
                    hint={t(
                      "game_editor.pred_claim_deadline_hint",
                      "Si renseignée, remplace la validité en jours. Affichée dans l'email du gagnant (« Expire le »).",
                    )}
                  >
                    <Input
                      type="date"
                      value={game.guaranteed_claim_deadline || ""}
                      onChange={(e) =>
                        patch({
                          guaranteed_claim_deadline: e.target.value || null,
                        })
                      }
                      className="rounded-xl"
                      data-testid="merchant-pred-claim-deadline"
                    />
                  </Field>
                </div>
                <Field
                  label={t(
                    "game_editor.pred_prize_instructions",
                    "Instructions du lot (email)",
                  )}
                >
                  <Textarea
                    value={game.guaranteed_prize_instructions || ""}
                    onChange={(e) =>
                      patch({
                        guaranteed_prize_instructions: e.target.value || null,
                      })
                    }
                    placeholder={t(
                      "game_editor.pred_prize_instructions_ph",
                      "Ex : Présentez cet email au comptoir.",
                    )}
                    rows={2}
                    className="rounded-xl"
                    data-testid="merchant-pred-prize-instructions"
                  />
                </Field>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  {t(
                    "lottery.emails_auto_note",
                    "Chaque gagnant reçoit automatiquement un email avec son lien de récupération.",
                  )}
                </p>
              </div>
            )}
          </Section>
        )}

        {/* ---------- Rules ---------- */}
        <Section title={t("merchant_editor.rules", "Règlement du jeu")}>
          <Field
            label={t("game_editor.rules_content", "Texte du règlement")}
            hint={t(
              "game_editor.rules_hint",
              "Ce texte est affiché sur la page publique quand un joueur clique sur « Règlement ».",
            )}
          >
            <Textarea
              value={game.rules_content || ""}
              onChange={(e) => patch({ rules_content: e.target.value })}
              rows={10}
              className="rounded-xl font-mono text-[13px]"
              data-testid="merchant-editor-rules"
            />
          </Field>
        </Section>

        {/* ---------- Read-only prizes summary ---------- */}
        {(game.segments || []).length > 0 && (
          <Section
            title={t("merchant_editor.prizes_locked", "Lots (verrouillés)")}
          >
            <div className="rounded-xl border border-border/70 bg-muted/30 px-4 py-3 text-[12.5px] text-muted-foreground mb-3 flex items-start gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                {t(
                  "merchant_editor.prizes_locked_hint",
                  "Pour garantir l'équité du tirage, seul votre administrateur peut modifier les lots, les probabilités ou le stock.",
                )}
              </div>
            </div>
            <div className="rounded-xl border border-border/60 divide-y divide-border/60 overflow-hidden">
              {game.segments.map((s, i) => (
                <div
                  key={i}
                  className="px-4 py-3"
                  data-testid={`merchant-editor-segment-${i}`}
                >
                  <div className="flex items-center gap-3 text-sm">
                    <input
                      type="color"
                      value={s.color || "#e2e8f0"}
                      onChange={(e) => patchSegmentColor(i, e.target.value)}
                      title={t(
                        "merchant_editor.segment_color_hint",
                        "Couleur du segment",
                      )}
                      className="h-6 w-6 rounded-md border border-black/10 shrink-0 cursor-pointer bg-transparent"
                      data-testid={`merchant-editor-segment-${i}-color`}
                    />
                    <span className="flex-1 truncate">{s.label}</span>
                    {s.is_winning ? (
                      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                        {t("common.winning", "Gagnant")}
                      </span>
                    ) : (
                      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {t("common.losing", "Perdant")}
                      </span>
                    )}
                    {s.initial_quantity != null && (
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {s.remaining_quantity ?? s.initial_quantity}/
                        {s.initial_quantity}
                      </span>
                    )}
                  </div>
                  {/* Per-prize email instructions — editable by merchants
                      ONLY on winning segments. Backend enforces that only
                      this field may differ from the admin-locked record. */}
                  {s.is_winning && (
                    <div className="mt-3 rounded-lg border border-amber-300/60 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-amber-700 dark:text-amber-300">
                          {t(
                            "merchant_editor.prize_instructions_badge",
                            "Email — info sur ce lot",
                          )}
                        </div>
                      </div>
                      <Textarea
                        value={s.instructions || ""}
                        onChange={(e) =>
                          patchSegmentInstructions(i, e.target.value)
                        }
                        placeholder={t(
                          "merchant_editor.prize_instructions_placeholder",
                          "Ex : Valable uniquement le mardi de 14h à 17h, sur place.",
                        )}
                        rows={2}
                        className="rounded-lg text-sm"
                        data-testid={`merchant-editor-segment-${i}-instructions`}
                      />
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {t(
                          "merchant_editor.prize_instructions_hint",
                          "Apparaît dans l'email de gain sous le code, en plus du message global du jeu.",
                        )}
                      </p>
                    </div>
                  )}
                  {/* Per-prize popup info — appears in the IN-APP win
                      pop-up, distinct from the email instructions. */}
                  {s.is_winning && (
                    <div className="mt-3 rounded-lg border border-sky-300/60 bg-sky-50/50 dark:bg-sky-500/5 p-3 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-sky-700 dark:text-sky-300">
                          {t(
                            "merchant_editor.prize_popup_badge",
                            "Détails supplémentaires",
                          )}
                        </div>
                      </div>
                      <Textarea
                        value={s.popup_info || ""}
                        onChange={(e) =>
                          patchSegmentPopupInfo(i, e.target.value)
                        }
                        placeholder={t(
                          "merchant_editor.prize_popup_placeholder",
                          "Ex : Rendez-vous en boutique à Paris 15e avec votre email.",
                        )}
                        rows={2}
                        className="rounded-lg text-sm"
                        data-testid={`merchant-editor-segment-${i}-popup-info`}
                      />
                      <p className="text-[11px] text-muted-foreground leading-snug">
                        {t(
                          "merchant_editor.prize_popup_hint",
                          "S'affiche dans la pop-up de gain à l'écran, immédiatement après la partie.",
                        )}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        <div className="flex justify-end mt-6">
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-xl h-11 px-6"
            data-testid="merchant-editor-save-btn-bottom"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1.5" />
            )}
            {t("common.save", "Enregistrer")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="qrise-card p-6 mb-4 space-y-4">
      <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-1.5 block">
        {label}
      </Label>
      {children}
      {hint && (
        <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
